import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/client";
import { isProjectAdmin } from "../utils/permissions";
import ProjectNav from "../components/ProjectNav";

const toPercent = (v: number) => +(v * 100).toFixed(1);
const fromPercent = (v: number) => +(v / 100).toFixed(4);

function PctInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number"
      step={0.1}
      value={toPercent(value)}
      onChange={e => onChange(fromPercent(parseFloat(e.target.value) || 0))}
      className="bg-transparent border-none outline-none font-semibold text-sm w-12 text-center p-0 focus:ring-0"
    />
  );
}

type TileColor = "blue" | "green" | "yellow" | "red";
const TILE_STYLE: Record<TileColor, string> = {
  blue:   "border-blue-300   bg-blue-50   text-blue-600",
  green:  "border-green-300  bg-green-50  text-green-600",
  yellow: "border-yellow-300 bg-yellow-50 text-yellow-600",
  red:    "border-red-300    bg-red-50    text-red-600",
};
const DOT: Record<TileColor, string> = {
  blue:   "bg-blue-500",
  green:  "bg-green-500",
  yellow: "bg-yellow-500",
  red:    "bg-red-500",
};

function Tile({ color, label, children }: { color: TileColor; label: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-xl border-2 ${TILE_STYLE[color]} px-4 py-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-2 h-2 rounded-full ${DOT[color]}`} />
        <span className="text-xs font-semibold">{label}</span>
      </div>
      <div className="text-sm font-semibold flex items-center gap-0.5">{children}</div>
    </div>
  );
}

export default function ProjectSettings() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [v, setV] = useState<Record<string, number>>({});
  const [orig, setOrig] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    if (!isProjectAdmin(projectId)) { navigate(`/dashboard/${projectId}`); return; }
    api.get(`/api/projects/${projectId}/config/thresholds`)
      .then(r => { setV(r.data); setOrig(r.data); setLoading(false); })
      .catch(() => navigate(`/dashboard/${projectId}`));
  }, [projectId]);

  const set = (key: string, val: number) => setV(p => ({ ...p, [key]: val }));
  const isDirty = JSON.stringify(v) !== JSON.stringify(orig);

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put(`/api/projects/${projectId}/config/thresholds`, v);
      setOrig({ ...v });
      setMsg({ text: "Thresholds saved. New reports will use these values.", ok: true });
    } catch { setMsg({ text: "Failed to save. Try again.", ok: false }); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ProjectNav projectId={projectId!} active="project" />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-sm text-gray-400">Loading thresholds…</p>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Thresholds & filters</h1>
          <p className="text-sm text-gray-500 mb-5">Default values for this project — can be overridden per report.</p>

          {/* Warning banner */}
          <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 mb-6">
            <svg className="w-4 h-4 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.636 6.364l.707-.707M6.343 6.343l-.707-.707" />
            </svg>
            <p className="text-sm text-orange-600">
              Changes here apply to upcoming reports only. Previously generated reports are not affected.
            </p>
          </div>

          {/* Row 1 — Flagging threshold + FTE */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Card 1: Threshold for flagging */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-700 mb-4">Threshold for flagging</p>
              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-2">Dollar amount</p>
                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50">
                    <span className="text-sm text-gray-500 mr-1">$</span>
                    <input
                      type="number"
                      step={1000}
                      value={Math.abs(v.cv_threshold ?? 500000)}
                      onChange={e => set("cv_threshold", -(parseFloat(e.target.value) || 0))}
                      className="bg-transparent border-none outline-none text-sm text-gray-800 w-full focus:ring-0"
                    />
                  </div>
                </div>
                <div className="w-28">
                  <p className="text-xs text-gray-400 mb-2">Or percentage</p>
                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50">
                    <input
                      type="number"
                      step={0.5}
                      min={0}
                      max={100}
                      value={v.variance_pct ?? 5}
                      onChange={e => set("variance_pct", parseFloat(e.target.value) || 0)}
                      className="bg-transparent border-none outline-none text-sm text-gray-800 w-full focus:ring-0"
                    />
                    <span className="text-sm text-gray-400 ml-1">%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: FTE */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-700 mb-1">Full-Time Equivalent (FTE)</p>
              <p className="text-xs text-gray-400 mb-4">1 FTE (Set the number of hours that defines 1 FTE)</p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50 gap-2">
                    <input
                      type="number"
                      step={1}
                      min={1}
                      max={80}
                      value={v.fte_hours_per_week ?? 30}
                      onChange={e => set("fte_hours_per_week", parseInt(e.target.value) || 0)}
                      className="bg-transparent border-none outline-none text-sm text-gray-800 w-full focus:ring-0"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">hrs/week</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50 gap-2">
                    <input
                      type="number"
                      step={1}
                      min={1}
                      max={300}
                      value={v.fte_hours_per_month ?? 160}
                      onChange={e => set("fte_hours_per_month", parseInt(e.target.value) || 0)}
                      className="bg-transparent border-none outline-none text-sm text-gray-800 w-full focus:ring-0"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">hrs/month</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 — CPI/SPI/CEI/Staffing tiles */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-8">
            <p className="text-sm font-semibold text-gray-700 mb-5">Threshold for flagging</p>

            {/* CPI + SPI */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* CPI */}
              <div>
                <p className="text-sm font-medium text-gray-600 mb-3">CPI thresholds</p>
                <div className="grid grid-cols-2 gap-2">
                  <Tile color="blue" label="Blue">
                    &gt;&nbsp;<PctInput value={v.cpi_blue ?? 1.045} onChange={val => set("cpi_blue", val)} />%
                  </Tile>
                  <Tile color="green" label="Green">
                    <PctInput value={v.cpi_green_min ?? 0.945} onChange={val => set("cpi_green_min", val)} />%
                    &nbsp;–&nbsp;
                    <PctInput value={v.cpi_green_max ?? 1.045} onChange={val => set("cpi_green_max", val)} />%
                  </Tile>
                  <Tile color="yellow" label="Yellow">
                    <PctInput value={v.cpi_yellow_min ?? 0.845} onChange={val => set("cpi_yellow_min", val)} />%
                    &nbsp;–&nbsp;
                    <PctInput value={v.cpi_yellow_max ?? 0.945} onChange={val => set("cpi_yellow_max", val)} />%
                  </Tile>
                  <Tile color="red" label="Red">
                    &lt;&nbsp;<PctInput value={v.cpi_red ?? 0.845} onChange={val => set("cpi_red", val)} />%
                  </Tile>
                </div>
              </div>

              {/* SPI */}
              <div>
                <p className="text-sm font-medium text-gray-600 mb-3">SPI thresholds</p>
                <div className="grid grid-cols-2 gap-2">
                  <Tile color="blue" label="Blue">
                    &gt;&nbsp;<PctInput value={v.spi_blue ?? 1.045} onChange={val => set("spi_blue", val)} />%
                  </Tile>
                  <Tile color="green" label="Green">
                    <PctInput value={v.spi_green_min ?? 0.945} onChange={val => set("spi_green_min", val)} />%
                    &nbsp;–&nbsp;
                    <PctInput value={v.spi_green_max ?? 1.045} onChange={val => set("spi_green_max", val)} />%
                  </Tile>
                  <Tile color="yellow" label="Yellow">
                    <PctInput value={v.spi_yellow_min ?? 0.845} onChange={val => set("spi_yellow_min", val)} />%
                    &nbsp;–&nbsp;
                    <PctInput value={v.spi_yellow_max ?? 0.945} onChange={val => set("spi_yellow_max", val)} />%
                  </Tile>
                  <Tile color="red" label="Red">
                    &lt;&nbsp;<PctInput value={v.spi_red ?? 0.845} onChange={val => set("spi_red", val)} />%
                  </Tile>
                </div>
              </div>
            </div>

            {/* CEI + Staffing */}
            <div className="grid grid-cols-2 gap-6">
              {/* CEI — display only (derived from CPI thresholds) */}
              <div>
                <p className="text-sm font-medium text-gray-600 mb-3">CEI thresholds</p>
                <div className="flex gap-2">
                  <Tile color="red" label="Red"><span className="text-sm font-semibold">&lt; 0.90</span></Tile>
                  <Tile color="yellow" label="Yellow"><span className="text-sm font-semibold">0.90 - 0.99</span></Tile>
                  <Tile color="green" label="Green"><span className="text-sm font-semibold">≥ 1.0</span></Tile>
                </div>
              </div>

              {/* Staffing — display only */}
              <div>
                <p className="text-sm font-medium text-gray-600 mb-3">Staffing thresholds</p>
                <div className="flex gap-2">
                  <Tile color="red" label="Unhealthy"><span className="text-sm font-semibold">0% – 50%</span></Tile>
                  <Tile color="yellow" label="Risk"><span className="text-sm font-semibold">51% – 75%</span></Tile>
                  <Tile color="green" label="Healthy"><span className="text-sm font-semibold">&gt; 76%</span></Tile>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          {msg && (
            <p className={`text-sm mb-3 ${msg.ok ? "text-green-600" : "text-red-500"}`}>{msg.text}</p>
          )}
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(`/dashboard/${projectId}`)}
              className="px-5 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl bg-white hover:bg-gray-50">
              Cancel
            </button>
            <div className="flex gap-3">
              <button onClick={() => navigate(`/dashboard/${projectId}`)}
                className="px-5 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl bg-white hover:bg-gray-50">
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="px-6 py-2 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
